import { defineComponent as _defineComponent } from 'vue'
import DialogContentLayout from "@/components/common/DialogContentLayout.vue";
import { URMRole, URMRoleKey, URMRoles } from "@/models/dto/userRoleManagement";
import { ContactGroup, Contact } from "@/models/dto/ContactGroup";
import UserRoleSelector from "@/components/UserRoleSelector.vue";
import ContactListItem from "@/components/Contact/ContactListItem.vue";
import ContactList from "@/components/Contact/ContactList.vue";
import { ref, computed, watch, getCurrentInstance, onUnmounted, nextTick } from "vue";
import {
  ContactAction,
  getContactServiceCall,
  getDisabledUserRoles,
} from "./utils/helpers";
import { useDisplay } from "@/composables/useDisplay";
import { UpdateContactRequestDTO } from "@/models/dto/contact/UpdateContactRequestDTO";
import { AddContactRequestDTO } from "@/models/dto/contact/AddContactRequestDTO";
import { ReservationDetail } from "@/models/dto";
import { formatReservationPickupDestinationText } from "@/utils/string";
import { DateTime } from "luxon";
import AlertBox from "@/components/AlertBox.vue";
import { ContactType } from "./utils/helpers";
import alert from "@/store/modules/alert";
import { isEqual } from "lodash";
import debounce from 'lodash/debounce';
import { validateEmail } from "@/utils/validators";



export default /*#__PURE__*/_defineComponent({
  __name: 'CreateEditContactContent',
  props: {
    title: null,
    subtitle: null,
    submitButtonText: null,
    isEditMode: { type: Boolean },
    contactGroups: null,
    contact: null,
    userRoles: null,
    defaultDisabledUserRoles: null,
    userTypeMessage: null,
    defaultUserType: null,
    preventDefaultHeaderClose: { type: Boolean },
    headerActionRightText: null,
    headerActionLeftText: null,
    tripCount: { default: 1 },
    currentTripIndex: { default: 0 },
    reservation: null,
    tripName: null,
    showTripName: { type: Boolean, default: false },
    contactListIsEditable: { type: Boolean, default: false },
    contactType: null,
    entityId: null
  },
  emits: [
  "remove:contact",
  "update:contact",
  "add:contact",
  "click:header-action-left",
  "click:header-action-right",
  "input",
  "update:tripName",
  "click:edit-contact",
  "click:remove-contact",
],
  setup(__props: any, { emit }) {

const props = __props as { title: string, subtitle?: string, submitButtonText: string, isEditMode: boolean, contactGroups: ContactGroup[], contact?: Contact, userRoles?: URMRole[], defaultDisabledUserRoles?: URMRole[], userTypeMessage?: string, defaultUserType?: URMRoleKey, preventDefaultHeaderClose?: boolean, headerActionRightText?: string, headerActionLeftText?: string, tripCount: number, currentTripIndex: number, reservation?: ReservationDetail, tripName?: string, showTripName: boolean, contactListIsEditable: boolean, contactType: ContactType, entityId: number };

const defaultUserRoles = [URMRoles.TripAuthorizedUser, URMRoles.TripViewOnly];




const { xs } = useDisplay();
const fullscreen = computed(() => xs.value);
const { proxy } = getCurrentInstance()!;
const t = proxy.$t.bind(proxy);

const userRoles = ref<URMRole[]>(props.userRoles || defaultUserRoles);
const email = ref("");
const tripName = ref(props.reservation?.routeName);

const submitted = ref(false);
const isSubmitting = ref(false);
const submissionError = ref("");
const valid = ref(false);


const disabledUserRoles = computed(() => {
  if (props.isEditMode && props.contact) {
    return getDisabledUserRoles(props.contact.currentUserPermissions) ?? []
  }
  return props.defaultDisabledUserRoles ?? []
})

const tripAuthorizedUser = URMRoles.TripAuthorizedUser.key;
const tripContactDisabled = computed(() =>
  disabledUserRoles.value.includes(URMRoles.TripContact)
);
const tripEditorDisabled = computed(() =>
  disabledUserRoles.value.includes(URMRoles.TripEditor)
);

const tripCountOfTotalText = computed(() => {
  return `Trip ${props.currentTripIndex + 1} of ${props.tripCount}`;
});

const pickupDropoffText = computed(() =>
  props.reservation
    ? formatReservationPickupDestinationText(props.reservation, t("common.TO"))
    : ""
);
const pickupDatetimeText = computed(() => {
  const firstStop = props.reservation?.stops?.[0];
  const pickupDatetime = firstStop?.pickupDatetime;
  const timeZone = firstStop?.address?.timeZone;
  if (!pickupDatetime || !timeZone) {
    return "";
  }
  return DateTime.fromISO(pickupDatetime, { zone: timeZone }).toFormat(
    "LL/dd/yy - hh:mm a"
  );
});

const form = ref({
  email: email.value,
  userType: props.contact?.roleKey || props.defaultUserType,
  allowModify: props.contact?.canModifyTrip ?? true,
  isTripContact: props.contact?.isTripContact ?? false,
  tripName: tripName.value,
});
// This is used to determine if the form has been modified
const initialForm = ref({ ...form.value });
const isDirty = ref(false);
watch(
  () => form.value,
  (newForm, oldForm) => {
    if (oldForm) { // Only check after initial setup
      isDirty.value = !isEqual(newForm, initialForm.value);
    }
  },
  { deep: true }
);

const vFormRef = ref();

const emailRules = [
  (v: string) => !!v || "Email is required",
  (v: string) => validateEmail(v) || "Email must be valid",
];

// Debounced validation function
const debouncedValidateEmail = debounce(() => {
  if (vFormRef.value && typeof vFormRef.value.validate === 'function') {
    vFormRef.value.validate();
  }
}, 250);

const onEmailInput = () => {
  debouncedValidateEmail();
};

const onEmailPaste = () => {
  // Validate immediately on paste
  if (vFormRef.value && typeof vFormRef.value.validate === 'function') {
    vFormRef.value.validate();
  }
};

watch(
  () => props.contact,
  (newContact) => {
    form.value = {
      email: newContact?.email || "",
      userType: newContact?.roleKey || props.defaultUserType,
      allowModify: newContact?.canModifyTrip ?? true,
      isTripContact: newContact?.isTripContact ?? false,
      tripName: props.reservation?.routeName,
    };
    initialForm.value = { ...form.value };
    if (vFormRef.value && typeof vFormRef.value.resetValidation === 'function') {
      vFormRef.value.resetValidation();
    }
  },
  { immediate: true, deep: true }
);

// Watchers for role logic
watch(
  [() => disabledUserRoles.value, () => form.value.userType],
  () => {
    if (
      !props.isEditMode &&
      disabledUserRoles.value.includes(URMRoles.TripContact)
    ) {
      form.value.isTripContact = false;
    }
    if (
      !props.isEditMode &&
      disabledUserRoles.value.includes(URMRoles.TripEditor)
    ) {
      form.value.allowModify = false;
    }
  },
  { immediate: true }
);

watch(
  [
    () => userRoles.value,
    () => props.defaultUserType,
    () => disabledUserRoles.value,
  ],
  ([roles, defaultUserType, disabledRoles]) => {
    if (form.value.userType) return;
    const hasValidDefault =
      defaultUserType &&
      roles.some((role) => role.key === defaultUserType) &&
      !disabledRoles.includes(URMRoles[defaultUserType]);
    const enabledRoles = roles.filter((role) => !disabledRoles.includes(role));
    const onlyOneEnabledRole = enabledRoles.length === 1;
    if (hasValidDefault) {
      form.value.userType = defaultUserType;
    } else if (onlyOneEnabledRole) {
      form.value.userType = enabledRoles[0].key;
    }
  },
  { immediate: true }
);

watch(
  () => props.isEditMode,
  (newValue) => {
    if (newValue && !props.contact?.canModifyTrip) {
      form.value.allowModify = false;
    }
  }
);

const onUserTypeUpdate = (newUserType) => {
  form.value.userType = newUserType;
  // When switching to Authorized User, default allowModify to true
  if (newUserType === tripAuthorizedUser) {
    form.value.allowModify = true;
  }
};

const resetForm = async () => {
  form.value = {
    email: "",
    userType: props.contact?.roleKey || props.defaultUserType,
    allowModify: props.contact?.canModifyTrip ?? true,
    isTripContact: props.contact?.isTripContact ?? false,
    tripName: props.reservation?.routeName,
  };
  submitted.value = false;
  isSubmitting.value = false;
  submissionError.value = "";

  isDirty.value = false;

  // Reset validation state
  if (vFormRef.value && typeof vFormRef.value.resetValidation === 'function') {
    vFormRef.value.resetValidation();
  }

  await nextTick();
  initialForm.value = { ...form.value };
};

const handleSubmit = async () => {
  submitted.value = true;
  submissionError.value = "";
  const isFormValid = await vFormRef.value?.validate();
  if (!isFormValid) return;

  isSubmitting.value = true;
  if (props.isEditMode) {
    handleUpdateContact();
  } else {
    handleCreateContact();
  }
};

const handleUpdateContact = async () => {
  const payload: UpdateContactRequestDTO = {
    roleKey: form.value.userType,
    isTripContact: form.value.isTripContact,
    externalUserId: props.contact?.externalId,
    canModifyTrip:
      form.value.userType === URMRoles.TripAuthorizedUser.key
        ? form.value.allowModify
        : false,
  };
  try {
    const updateContactFn = getContactServiceCall(
      props.contactType,
      ContactAction.Update
    )
    await updateContactFn(props.entityId, props.contact?.externalId!, payload);
    alert.add({
      text: "",
      color: "success",
      title: "Updated",
      dismissible: true,
    });

    emit("update:contact");
  } catch (error: any) {
    console.error(
      `Updating Contact ${props.contact?.externalId} to ${JSON.stringify(
        payload
      )} to ${props.contactType} ${
        props.entityId
      } from CreateEditContactContent failed: ${error.response.data.message}`
    );
    submissionError.value = "Updating Contact failed";
  } finally {
    isSubmitting.value = false;
    submitted.value = false;
  }
};

const handleCreateContact = async () => {
  const payload: AddContactRequestDTO = {
    isTripContact: form.value.isTripContact,
    email: form.value.email,
    roleKey: form.value.userType,
    canModifyTrip:
      form.value.userType === URMRoles.TripAuthorizedUser.key
        ? form.value.allowModify
        : false,
  };
  const addContactFn = getContactServiceCall(
    props.contactType,
    ContactAction.Add
  )
  try {
    if (form.value.email) {
      await addContactFn(props.entityId, payload);
      alert.add({
        text: "",
        color: "success",
        title: "Added",
        dismissible: true,
      });
      emit("add:contact");
      await resetForm();
    }
  } catch (error: any) {
    console.error(
      `Adding New Contact ${JSON.stringify(payload)} to ${props.contactType} ${
        props.entityId
      } from CreateEditContactContent failed: ${error.response.data.message}`
    );
    submissionError.value = "Adding New Contact failed";
  } finally {
    isSubmitting.value = false;
    submitted.value = false;
  }
};

watch(
  () => form.value.tripName,
  (val) => {
    emit("update:tripName", val);
  }
);

const handleEditContactClick = (contact: Contact) => {
  emit("click:edit-contact", contact);
};

const handleRemoveContactClick = (contact: Contact) => {
  emit("click:remove-contact", contact);
};

onUnmounted(() => {
  debouncedValidateEmail.cancel();
});

return { __sfc: true,defaultUserRoles, props, emit, xs, fullscreen, proxy, t, userRoles, email, tripName, submitted, isSubmitting, submissionError, valid, disabledUserRoles, tripAuthorizedUser, tripContactDisabled, tripEditorDisabled, tripCountOfTotalText, pickupDropoffText, pickupDatetimeText, form, initialForm, isDirty, vFormRef, emailRules, debouncedValidateEmail, onEmailInput, onEmailPaste, onUserTypeUpdate, resetForm, handleSubmit, handleUpdateContact, handleCreateContact, handleEditContactClick, handleRemoveContactClick, DialogContentLayout, UserRoleSelector, ContactListItem, ContactList, AlertBox }
}

})